import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Button } from '@agms/ui'
import { PageHeader, SurfaceCard } from '../components/ui'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import { usePermissions } from '../hooks/useAuth'
import api from '../lib/api'
import { useState } from 'react'

interface Revision {
  id: string
  label: string | null
  brand: string | null
  source_url: string | null
  sha256: string
  byte_size: number
  http_status: number | null
  status: string
  note: string | null
  has_file: boolean
}

export function SettingsInterchangeReviewPage() {
  const { canManageScheduleA } = usePermissions()
  const canReview = canManageScheduleA()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const revisions = useQuery({
    queryKey: ['interchange-reviews'],
    queryFn: async () => (await api.get<{ data: Revision[] }>('/settings/interchange-reviews')).data.data,
    enabled: canReview,
  })

  const acknowledge = useMutation({
    mutationFn: async (id: string) => api.post(`/settings/interchange-reviews/${id}/acknowledge`),
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['interchange-reviews'] })
    },
    onError: (caught) => setError(apiErrorMessage(caught, 'The document could not be acknowledged.')),
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Interchange document review"
        subtitle="A changed public Visa or Mastercard document is flagged here. Acknowledging it does not change stored rates."
        action={<Link to="/settings" className="text-sm text-sky-700">Settings</Link>}
      />

      {!canReview ? (
        <p className="text-sm text-gray-600">Only an admin can review interchange documents.</p>
      ) : null}
      {error ? <p className="text-sm text-red-700" role="alert">{error}</p> : null}

      {canReview && revisions.data?.length === 0 ? (
        <p className="text-sm text-gray-500">No interchange document revisions yet.</p>
      ) : null}

      <div className="space-y-4">
        {revisions.data?.map((revision) => (
          <SurfaceCard key={revision.id}>
            <h2 className="text-sm font-semibold text-gray-900">{revision.label || revision.brand}</h2>
            <p className="mt-1 text-xs text-gray-500">{revision.status} · HTTP {revision.http_status ?? '-'} · {revision.byte_size} bytes</p>
            {revision.note ? <p className="mt-2 text-sm text-gray-700">{revision.note}</p> : null}
            <p className="mt-2 break-all text-xs text-gray-500">{revision.sha256}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {revision.has_file ? (
                <a className="text-sm text-sky-700" href={`/api/settings/interchange-reviews/${revision.id}/file`}>
                  Download
                </a>
              ) : null}
              {canReview && revision.status === 'pending_review' ? (
                <Button type="button" onClick={() => acknowledge.mutate(revision.id)} disabled={acknowledge.isPending}>
                  Acknowledge
                </Button>
              ) : null}
            </div>
          </SurfaceCard>
        ))}
      </div>
    </div>
  )
}
